import { useState } from "react";
import type { Translate } from "../i18n/hooks.ts";
import type { TextKey } from "../i18n/en.ts";
import type { ElementCategory, ElementRef } from "../model/inp/properties.ts";
import type { SwmmProject } from "../model/inp/types.ts";
import {
  deleteElement,
  linksAttachedToNode,
  nodesOrphanedByDeletingLink,
  planGroupDelete,
  subcatchmentsUsingAsOutlet,
  subcatchmentsUsingRaingage,
} from "../model/inp/editNetwork.ts";
import { controlRuleReferencesElement } from "../engine/controlRules/controlRuleParser.ts";
import { parseControlRulesConfig } from "../engine/routing/routingShared.ts";

/**
 * Every `[CONTROLS]` RULE naming `name` (a node/link/raingage, never a
 * subcatchment — no real `[CONTROLS]` object keyword refers to one) — real
 * `controls.c` rejects the whole `.inp` at load with `ERR_NAME` on a
 * dangling reference, this codebase has no fatal-load-error path, so
 * `handleDelete` below blocks the delete instead, same as every other
 * still-referenced-elsewhere check in this file. Reuses the engine's own
 * parser rather than a naive text search, so it only catches rule forms
 * Phase 1 actually understands — the same limitation the routing engine
 * itself already has for these rules.
 */
function controlRulesReferencing(
  project: SwmmProject,
  category: "node" | "link" | "raingage",
  name: string
): string[] {
  const { parsedControlRules } = parseControlRulesConfig(project.rawSections, new Set());
  return parsedControlRules
    .filter((rule) => controlRuleReferencesElement(rule, category, name))
    .map((rule) => rule.name);
}

/** What `handleDelete`'s connectivity check decided — "blocked" renders AlertDialog (e.g. a node still attached to a link), "confirm" renders ConfirmDialog. */
export type PendingDelete =
  | { kind: "blocked"; title: string; message: string }
  | { kind: "confirm"; title: string; message: string; onConfirm: () => void };

/** Always the CATEGORY word (node/link/...), never a specific kind (junction/outfall/...) — found live: showing the specific kind next to a plain node id read as two unrelated words for the same thing. */
const CATEGORY_LABEL_KEY: Record<ElementCategory, TextKey> = {
  node: "category.node",
  link: "category.link",
  subcatchment: "category.subcatchment",
  raingage: "category.raingage",
};

/** Longer than this and the dialog just dumps an unreadable wall of names (found live: a raingage feeding ~650 subcatchments). */
const MAX_JOINED_NAMES = 8;

/** '"16"' / '"16", "17" and "18"' / '"16", "17", ..., "23" and 42 more' — natural quoted-list joining, no Oxford comma. */
function joinQuoted(names: string[], t: Translate): string {
  const shown = names.slice(0, MAX_JOINED_NAMES).map((n) => `"${n}"`);
  const remaining = names.length - shown.length;
  if (remaining > 0) return `${shown.join(", ")} ${t("common.andNMore", { count: remaining })}`;
  if (shown.length <= 1) return shown.join("");
  return `${shown.slice(0, -1).join(", ")} ${t("common.and")} ${shown[shown.length - 1]}`;
}

/**
 * The whole-selection delete flow — from the toolbar/MultiSelectBar button
 * or the Delete key (App.tsx's own useKeyboardShortcuts.ts) down to the
 * AlertDialog/ConfirmDialog that `pendingDelete` drives. Never deletes
 * anything itself outside `onConfirm`; App.tsx renders the dialogs.
 */
export function useDeleteFlow(
  project: SwmmProject | null,
  selected: ElementRef | null,
  multiSelected: ElementRef[],
  t: Translate,
  mutateRawUndoable: (
    fn: (raw: ReadonlyMap<string, string[][]>) => ReadonlyMap<string, string[][]>
  ) => void,
  setSelected: (ref: ElementRef | null) => void,
  setMultiSelected: (refs: ElementRef[]) => void
) {
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);

  /** Deletes every currently-selected object at once, regardless of mix of kinds. `planGroupDelete` does the actual connectivity reasoning; this just turns its result into `pendingDelete` dialog state — never deletes anything itself. */
  function handleGroupDelete() {
    if (!project) return;
    const selection = multiSelected;
    const plan = planGroupDelete(project, selection);

    if ("blockedNodes" in plan) {
      setPendingDelete({
        kind: "blocked",
        title: t("delete.blockedGroupTitle"),
        message: t("delete.blockedGroupMessage", {
          items: joinQuoted(
            plan.blockedNodes.map((n) => n.name),
            t
          ),
        }),
      });
      return;
    }

    const { orphanedNodes } = plan;
    setPendingDelete({
      kind: "confirm",
      title: t("delete.confirmGroupTitle", { count: selection.length }),
      message:
        orphanedNodes.length > 0
          ? t("delete.confirmGroupOrphanWarning", {
              count: selection.length,
              items: joinQuoted(
                orphanedNodes.map((n) => n.name),
                t
              ),
            })
          : t("delete.confirmGroupMessage", { count: selection.length }),
      onConfirm: () => {
        mutateRawUndoable((raw) => {
          let next: ReadonlyMap<string, string[][]> = raw;
          for (const ref of selection) next = deleteElement(next, ref);
          for (const node of orphanedNodes) {
            next = deleteElement(next, { category: "node", kind: node.kind, name: node.name });
          }
          return next;
        });
        setSelected(null);
        setMultiSelected([]);
      },
    });
  }

  /**
   * Figures out what deleting `selected` requires and stores it in
   * `pendingDelete` for the dialog below to act on — never deletes anything
   * itself. A node currently attached to any link is BLOCKED outright rather
   * than silently taking the links with it; deleting a link instead checks
   * the OTHER direction — whether either end node would be left with zero
   * remaining links — and folds that into the same confirmation. 2+ selected
   * elements delegates to `handleGroupDelete` instead.
   */
  function handleDelete() {
    if (multiSelected.length > 1) {
      handleGroupDelete();
      return;
    }
    if (!selected || !project) return;
    // Fresh `const` bindings so their narrowed (non-null) type survives
    // capture by the nested function declarations below — a closure over the
    // `selected`/`project` parameters themselves loses that narrowing.
    const currentSelected = selected;
    const currentProject = project;
    const categoryLabel = t(CATEGORY_LABEL_KEY[currentSelected.category]);
    const name = currentSelected.name;

    function block(message: string) {
      setPendingDelete({
        kind: "blocked",
        title: t("delete.blockedTitle", { category: categoryLabel }),
        message,
      });
    }

    function confirmDelete(message: string, onConfirm: () => void) {
      setPendingDelete({
        kind: "confirm",
        title: t("delete.confirmTitle", { category: categoryLabel }),
        message,
        onConfirm,
      });
    }

    function deleteSelectedAndClear() {
      mutateRawUndoable((raw) => deleteElement(raw, currentSelected));
      setSelected(null);
      setMultiSelected([]);
    }

    /** node/link/raingage's common "still named by a [CONTROLS] rule" check — returns true (and calls `block`) when blocked, so each branch below can `if (blockOnControlRules(...)) return;` right alongside its own existing checks. */
    function blockOnControlRules(category: "node" | "link" | "raingage"): boolean {
      const rules = controlRulesReferencing(currentProject, category, name);
      if (rules.length === 0) return false;
      const items = joinQuoted(rules, t);
      block(
        t(rules.length === 1 ? "delete.blockedControlRuleOne" : "delete.blockedControlRuleMany", {
          name,
          items,
        })
      );
      return true;
    }

    /** A node or subcatchment still named as some OTHER subcatchment's Outlet column used to be deletable outright, producing an export real SWMM rejects at load time — blocked here instead, same as an attached link. */
    function blockOnOutletUsage(): boolean {
      const outletUsers = subcatchmentsUsingAsOutlet(currentProject, name);
      if (outletUsers.length === 0) return false;
      const items = joinQuoted(
        outletUsers.map((s) => s.name),
        t
      );
      block(
        t(
          outletUsers.length === 1 ? "delete.blockedOutletUsedOne" : "delete.blockedOutletUsedMany",
          { name, items }
        )
      );
      return true;
    }

    if (currentSelected.category === "node") {
      const attached = linksAttachedToNode(currentProject, name);
      if (attached.length > 0) {
        const items = joinQuoted(
          attached.map((l) => l.name),
          t
        );
        block(
          t(
            attached.length === 1
              ? "delete.blockedNodeConnectedOne"
              : "delete.blockedNodeConnectedMany",
            { name, items }
          )
        );
        return;
      }
      if (blockOnOutletUsage()) return;
      if (blockOnControlRules("node")) return;
      confirmDelete(
        t("delete.confirmMessage", { category: categoryLabel, name }),
        deleteSelectedAndClear
      );
      return;
    }

    if (currentSelected.category === "link") {
      if (blockOnControlRules("link")) return;
      const link = currentProject.links.find((l) => l.name === name);
      const orphaned = link ? nodesOrphanedByDeletingLink(currentProject, link) : [];
      confirmDelete(
        orphaned.length > 0
          ? t(
              orphaned.length === 1
                ? "delete.confirmLinkOrphanWarningOne"
                : "delete.confirmLinkOrphanWarningMany",
              {
                name,
                items: joinQuoted(
                  orphaned.map((n) => n.name),
                  t
                ),
              }
            )
          : t("delete.confirmMessage", { category: categoryLabel, name }),
        () => {
          mutateRawUndoable((raw) => {
            let next = deleteElement(raw, currentSelected);
            for (const node of orphaned) {
              next = deleteElement(next, { category: "node", kind: node.kind, name: node.name });
            }
            return next;
          });
          setSelected(null);
          setMultiSelected([]);
        }
      );
      return;
    }

    // A raingage still named as some subcatchment's Raingage column used to
    // be deletable outright — real SWMM rejects that export at load time.
    if (currentSelected.category === "raingage") {
      const users = subcatchmentsUsingRaingage(currentProject, name);
      if (users.length > 0) {
        const items = joinQuoted(
          users.map((s) => s.name),
          t
        );
        block(
          t(
            users.length === 1 ? "delete.blockedRaingageUsedOne" : "delete.blockedRaingageUsedMany",
            { name, items }
          )
        );
        return;
      }
      if (blockOnControlRules("raingage")) return;
    }

    // Same rule as the node case above — a subcatchment's Outlet column can
    // ALSO name another subcatchment, not just a node.
    if (currentSelected.category === "subcatchment" && blockOnOutletUsage()) return;

    confirmDelete(
      t("delete.confirmMessage", { category: categoryLabel, name }),
      deleteSelectedAndClear
    );
  }

  return { pendingDelete, setPendingDelete, handleDelete, handleGroupDelete };
}
